import { useEffect, useState } from "react";
import { IBasket } from "../models/IBasket";
import { IFood, IFoodMenuUpgrade } from "../models/IFood";
import { IMenuUpgrade } from "../models/IOrder";
import { S3_IMAGE_BASE_URL } from "../services/config";
import { BASKET_EVENT } from "../utils/BasketContext";
import { useLanguage } from "../context/LanguageContext";
import "../styles/style.css";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

interface Props {
  food: IFood;
  minAmount: number;
  onClose: () => void;
}

const TWO_HOURS = 2 * 60 * 60 * 1000;

function MobileFoodDetailPopup({ food, minAmount, onClose }: Props) {
  useBodyScrollLock();
  const { t, lang } = useLanguage();
  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState("");
  const [selectedUpgrades, setSelectedUpgrades] = useState<Record<string, number>>({});
  const [showConflict, setShowConflict] = useState(false);
  const [added, setAdded] = useState(false);
  const [closing, setClosing] = useState(false);

  const handleClose = () => {
    setClosing(true);
    setTimeout(onClose, 300);
  };

  useEffect(() => {
    setQuantity(1);
    setNote("");
    setShowConflict(false);
    setAdded(false);
    const autoSelected: Record<string, number> = {};
    food.menuUpgrade
      .filter((opt) => opt.menuUpgradeIsEnabled)
      .forEach((opt) => {
        if (opt.menuUpgradeMaxQuantity === 1 && opt.menuUpgradePrice === 0) {
          autoSelected[opt.id] = 1;
        }
      });
    setSelectedUpgrades(autoSelected);
  }, [food]);

  const enabledUpgrades = food.menuUpgrade.filter((opt) => opt.menuUpgradeIsEnabled);
  const extraCost = enabledUpgrades.reduce(
    (s, opt) => s + opt.menuUpgradePrice * (selectedUpgrades[opt.id] ?? 0),
    0
  );
  // Menu upgrades are never discounted — only the dish itself.
  const unitPrice = food.hasDiscount && food.discountedPrice != null ? food.discountedPrice : food.foodPrice;
  const total = (unitPrice + extraCost) * quantity;
  const foodName = lang === "en" ? food.foodNameEn : food.foodNameTr;

  const toggleUpgrade = (opt: IFoodMenuUpgrade, checked: boolean) => {
    setSelectedUpgrades((prev) => ({ ...prev, [opt.id]: checked ? 1 : 0 }));
  };

  const changeUpgradeQty = (opt: IFoodMenuUpgrade, delta: number) => {
    setSelectedUpgrades((prev) => ({
      ...prev,
      [opt.id]: Math.min(opt.menuUpgradeMaxQuantity, Math.max(0, (prev[opt.id] ?? 0) + delta)),
    }));
  };

  const doAdd = (forceReplace = false) => {
    const stored = localStorage.getItem("basket");
    let basket: IBasket;
    try {
      basket = stored
        ? JSON.parse(stored)
        : { restourantId: food.restourantId, lastUpdated: Date.now(), minAmount, items: [] };
    } catch {
      basket = { restourantId: food.restourantId, lastUpdated: Date.now(), minAmount, items: [] };
    }

    const expired = basket.lastUpdated && Date.now() - basket.lastUpdated > TWO_HOURS;
    const conflict = basket.restourantId !== food.restourantId && basket.items.length > 0 && !expired;

    if (conflict && !forceReplace) {
      setShowConflict(true);
      return;
    }

    if (basket.restourantId !== food.restourantId || expired) {
      basket = { restourantId: food.restourantId, lastUpdated: Date.now(), minAmount, items: [] };
    }

    basket.minAmount = minAmount;
    const upgrades: IMenuUpgrade[] = enabledUpgrades
      .filter((opt) => (selectedUpgrades[opt.id] ?? 0) > 0)
      .map((opt) => ({
        menuUpgradeTitle: opt.menuUpgradeTitle,
        menuUpgradeQuantity: selectedUpgrades[opt.id],
        menuUpgradePrice: opt.menuUpgradePrice,
      }));

    basket.items.push({
      foodId: food.id,
      foodName,
      foodImagePath: food.foodImagePath,
      foodPrice: food.foodPrice,
      discountedPrice: food.hasDiscount ? food.discountedPrice : undefined,
      foodDiscountId: food.hasDiscount ? food.activeDiscount?.id : undefined,
      foodQuantity: quantity,
      foodNote: note,
      menuUpgrade: upgrades,
    });
    basket.lastUpdated = Date.now();
    localStorage.setItem("basket", JSON.stringify(basket));
    window.dispatchEvent(new CustomEvent(BASKET_EVENT));
    setShowConflict(false);
    setAdded(true);
    setTimeout(handleClose, 600);
  };

  return (
    <div className={`mobile-bottom-popup-overlay${closing ? " mobile-bottom-popup-overlay--closing" : ""}`} onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}>
      <div className={`mobile-bottom-popup ${closing ? "mobile-bottom-popup--closing" : "mobile-bottom-popup--animate"}`} style={{ maxHeight: "92vh" }}>
        {/* Kapat butonu */}
        <button
          className="position-absolute d-flex align-items-center justify-content-center"
          style={{
            top: 10,
            left: 10,
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "rgba(0,0,0,0.5)",
            color: "#fff",
            border: "none",
            fontSize: "1rem",
            zIndex: 2,
          }}
          onClick={handleClose}
          aria-label={t("common.close")}
        >
          <i className="bi bi-x-lg"></i>
        </button>

        {/* Hero resim */}
        {food.foodImagePath && (
          <div style={{ width: "100%", height: "220px", overflow: "hidden" }}>
            <img
              src={S3_IMAGE_BASE_URL + food.foodImagePath}
              alt={foodName}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
        )}

        {/* İçerik */}
        <div className="px-4 pt-3 pb-2" style={{ overflowY: "auto", maxHeight: food.foodImagePath ? "calc(92vh - 220px - 70px)" : "calc(92vh - 70px)" }}>
          <h5 className="fw-bold mb-1">{foodName}</h5>
          <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
            {food.hasDiscount && food.discountedPriceText ? (
              <>
                <s className="text-muted" style={{ fontSize: "0.85rem" }}>
                  {food.foodPriceText} {food.currency.currencyDisplayText}
                </s>
                <span className="fw-bold" style={{ color: "#c20e1a", fontSize: "1.1rem" }}>
                  {food.discountedPriceText} {food.currency.currencyDisplayText}
                </span>
                <span className="badge bg-warning text-dark" style={{ fontSize: "0.65rem" }}>
                  <i className="bi bi-percent"></i> {t("food.campaign")}
                </span>
              </>
            ) : (
              <span className="fw-bold" style={{ color: "#c20e1a", fontSize: "1.1rem" }}>
                {food.foodPriceText} {food.currency.currencyDisplayText}
              </span>
            )}
          </div>
          {food.foodContents && (
            <p className="text-muted small mb-3">{food.foodContents}</p>
          )}

          {/* Ekstralar */}
          {enabledUpgrades.length > 0 && (
            <div className="mb-3">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <span className="fw-semibold">{t("food.extras")}</span>
              </div>
              {enabledUpgrades.map((opt) => (
                <div key={opt.id} className="py-2" style={{ borderBottom: "1px solid #f0f0f0" }}>
                  {opt.menuUpgradeMaxQuantity === 1 ? (
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <span className="small">{opt.menuUpgradeTitle}</span>
                        {opt.menuUpgradePrice > 0 && (
                          <span className="small fw-semibold ms-2" style={{ color: "#c20e1a" }}>
                            +{opt.menuUpgradePriceText}₺
                          </span>
                        )}
                      </div>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        style={{ width: 22, height: 22 }}
                        checked={(selectedUpgrades[opt.id] ?? 0) === 1}
                        onChange={(e) => toggleUpgrade(opt, e.target.checked)}
                      />
                    </div>
                  ) : (
                    <div>
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <span className="small">{opt.menuUpgradeTitle}</span>
                          {opt.menuUpgradePrice > 0 && (
                            <span className="small fw-semibold ms-2" style={{ color: "#c20e1a" }}>
                              +{opt.menuUpgradePriceText}₺
                            </span>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: 28, height: 28, border: "1px solid #ddd", padding: 0 }}
                            onClick={() => changeUpgradeQty(opt, -1)}
                          >
                            <i className="bi bi-dash"></i>
                          </button>
                          <span className="fw-bold" style={{ minWidth: 16, textAlign: "center" }}>
                            {selectedUpgrades[opt.id] ?? 0}
                          </span>
                          <button
                            type="button"
                            className="btn btn-sm rounded-circle d-flex align-items-center justify-content-center"
                            style={{ width: 28, height: 28, border: "1px solid #ddd", padding: 0 }}
                            onClick={() => changeUpgradeQty(opt, 1)}
                          >
                            <i className="bi bi-plus"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Conflict uyarısı */}
          {showConflict && (
            <div className="alert alert-warning py-2 small">
              <p className="mb-2">
                <i className="bi bi-exclamation-triangle me-1"></i>
                {t("food.conflictWarning")}
              </p>
              <div className="d-flex gap-2">
                <button className="btn btn-sm btn-danger rounded-pill px-3" onClick={() => doAdd(true)}>
                  {t("food.conflictConfirm")}
                </button>
                <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" onClick={() => setShowConflict(false)}>
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}

          {/* Not */}
          <div className="mb-3">
            <label className="form-label small fw-semibold text-muted">{t("food.customerNote")}</label>
            <input
              type="text"
              className="form-control form-control-sm rounded-3"
              placeholder="..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        {/* Alt sabit bar: adet + sepete ekle */}
        <div
          className="d-flex align-items-center gap-3 px-4 py-3"
          style={{ borderTop: "1px solid #f0f0f0", background: "#fff", paddingBottom: "calc(12px + env(safe-area-inset-bottom))" }}
        >
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 36, height: 36, border: "2px solid #ddd", padding: 0, fontSize: "1.1rem" }}
              onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            >
              <i className="bi bi-dash"></i>
            </button>
            <span className="fw-bold fs-5" style={{ minWidth: 24, textAlign: "center" }}>{quantity}</span>
            <button
              className="btn rounded-circle d-flex align-items-center justify-content-center"
              style={{ width: 36, height: 36, border: "2px solid #ddd", padding: 0, fontSize: "1.1rem" }}
              onClick={() => setQuantity((q) => q + 1)}
            >
              <i className="bi bi-plus"></i>
            </button>
          </div>

          <button
            className="btn flex-grow-1 fw-bold py-2"
            style={{
              background: added ? "#28a745" : "#c20e1a",
              color: "#fff",
              borderRadius: 14,
              fontSize: "0.95rem",
              transition: "background 0.3s",
            }}
            onClick={() => doAdd(false)}
            disabled={added}
          >
            {added ? (
              <><i className="bi bi-check-lg me-1"></i>{t("food.addToCart")}</>
            ) : (
              <>{t("food.addToCart")} · {total.toFixed(2)} {food.currency.currencyDisplayText}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MobileFoodDetailPopup;
